<template>
  <div id="PrivacyPolicy">
    <b-container class="topics_container privacy-container privacy-topbar">
      <div class="topics_back_button" @click="back">
        <b-icon-caret-left aria-hidden="true" /> Back
      </div>
    </b-container>

    <b-container class="privacy-container">
      <div class="privacy-content">
      <h1>Privacy Policy</h1>
      <p>Last updated: April 18, 2026</p>

      <p>
        Learn with Engram respects your privacy and is committed to being clear
        about what information we collect, how we use it, and what choices you
        have when using this website.
      </p>

      <p>
        We use necessary cookies for core functionality such as authentication,
        security, and reliable site performance. Optional analytics cookies are
        only enabled if you choose to accept them.
      </p>

      <p>
        You can change your cookie preferences at any time through
        <strong>Privacy settings</strong> in the site footer.
      </p>

      <h2>Overview</h2>
      <p>
        This Privacy Policy explains how Learn with Engram ("we," "us," or
        "our") collects, uses, and protects information when you use this
        website and its learning tools (the "Service").
      </p>

      <h2>Information We Collect</h2>

      <h3>Information You Provide</h3>
      <p>
        Depending on how you use the Service, you may provide information such
        as:
      </p>
      <ul>
        <li>Written responses, essays, or other study-related submissions</li>
        <li>Account information, such as your email address, if login is enabled</li>
        <li>Messages or requests you send to us directly</li>
      </ul>

      <h3>Information Collected Automatically</h3>
      <p>
        We may automatically collect limited technical and usage information
        when you use the Service, including:
      </p>
      <ul>
        <li>IP address</li>
        <li>Browser type and version</li>
        <li>Device type</li>
        <li>Pages visited and time spent on the site</li>
        <li>General diagnostic and performance data</li>
      </ul>

      <h2>How We Use Information</h2>
      <p>We use information we collect to:</p>
      <ul>
        <li>Provide and maintain the Service</li>
        <li>Deliver essay feedback and other learning features</li>
        <li>Improve site functionality, usability, and performance</li>
        <li>Monitor security and prevent abuse or misuse</li>
        <li>Understand general usage trends if analytics are enabled</li>
        <li>Respond to questions, requests, or support messages</li>
      </ul>

      <p>
        We do <strong>not</strong> sell your personal data.
      </p>

      <h2>AI-Generated Feedback</h2>
      <p>
        Some features of the Service may use third-party artificial intelligence
        tools to generate feedback on student writing or other submitted content.
        By using those features, you understand that submitted content may be
        processed for the purpose of generating responses and improving the
        functionality of the Service.
      </p>

      <h2>Cookies and Tracking Technologies</h2>
      <p>
        We use cookies and similar technologies in a limited way to support the
        operation of the website.
      </p>

      <ul>
        <li>
          <strong>Essential cookies:</strong> These are required for core site
          functionality such as login, security, and maintaining active sessions.
        </li>
        <li>
          <strong>Preference cookies:</strong> These may remember settings such
          as consent choices or other site preferences.
        </li>
        <li>
          <strong>Optional analytics cookies:</strong> These help us understand
          how visitors use the site, but they are only enabled after you provide
          consent.
        </li>
      </ul>

      <p>
        You can manage cookies through your browser settings and, where
        available, through the site’s privacy controls.
      </p>

      <h2>Third-Party Services</h2>
      <p>
        We may use trusted third-party service providers to help operate the
        Service, such as hosting, database, authentication, analytics, and
        AI-based tools. These providers may process data only as needed to
        support the Service.
      </p>

      <h2>Sharing of Information</h2>
      <p>We may share information in limited circumstances, including:</p>
      <ul>
        <li>With service providers who help us operate the website</li>
        <li>To comply with legal obligations</li>
        <li>To protect the rights, safety, and security of the Service or others</li>
        <li>
          In connection with a business transfer such as a merger, acquisition,
          or asset sale
        </li>
        <li>With your consent or at your direction</li>
      </ul>

      <h2>Data Retention</h2>
      <p>
        We retain information only for as long as reasonably necessary to
        provide the Service, improve its functionality, maintain security, and
        comply with legal obligations.
      </p>

      <h2>Data Security</h2>
      <p>
        We take reasonable steps to protect your information. However, no method
        of transmission over the internet or method of electronic storage is
        completely secure, and we cannot guarantee absolute security.
      </p>

      <h2>Children’s Privacy</h2>
      <p>
        This Service may be used by students and educators. We do not knowingly
        collect personal data from children under 13 without appropriate consent
        where required by law. If you believe a child has provided personal
        information inappropriately, please contact us and we will review and
        address the issue.
      </p>

      <h2>Links to Other Websites</h2>
      <p>
        The Service may contain links to third-party websites. We are not
        responsible for the privacy practices or content of those external
        sites. We encourage you to review their privacy policies separately.
      </p>

      <h2>Your Choices</h2>
      <p>
        Depending on your location and how you use the Service, you may have the
        right to request access to, correction of, or deletion of your personal
        information, and to withdraw consent for optional data collection.
      </p>

      <h2>Changes to This Privacy Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. When we do, we will
        post the updated version on this page and revise the "Last updated"
        date above.
      </p>
      <p>
        We encourage you to review this Privacy Policy periodically to stay
        informed about our practices and your choices.
      </p>
      </div>
    </b-container>
  </div>
</template>

<script>
import { goToHome } from "@/router/navigation";

export default {
  name: "PrivacyPolicy",
  methods: {
    back() {
      if (window.history.length > 1) {
        this.$router.go(-1);
        return;
      }

      goToHome();
    }
  },
  mounted() {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }
};
</script>
<style lang="sass" scoped src="@/assets/css/topics.sass"></style>
<style scoped>
#PrivacyPolicy {
  min-height: 100vh;
  background: #f6f7fb;
}

.privacy-container {
  max-width: 980px;
}

.privacy-topbar {
  padding-top: 24px;
  padding-bottom: 10px;
}

.privacy-content {
  background: #ffffff;
  border: 1px solid #e3e8f2;
  border-radius: 16px;
  padding: 28px 30px 34px;
  margin-bottom: 36px;
}

.privacy-content h1,
.privacy-content h2,
.privacy-content h3 {
  color: #1f2430;
}

.privacy-content h1 {
  margin-bottom: 10px;
}

.privacy-content h2 {
  margin-top: 28px;
}

.privacy-content p,
.privacy-content li {
  color: #2d3445;
  line-height: 1.7;
}

.topics_back_button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
}

@media (max-width: 768px) {
  .privacy-topbar {
    padding-top: 16px;
    padding-bottom: 8px;
  }

  .privacy-content {
    padding: 20px 18px 24px;
    border-radius: 12px;
  }
}
</style>
